import { z } from "zod";

/**
 * Zod contracts mirrored from @nacs/shared (`dto/common.ts`, `dto/auth.ts`, `dto/identity.ts`, `dto/devices.ts`).
 * Deviation, recorded: the package is not yet published to GitHub Packages, so these are copied
 * verbatim. When it is, replace this file with `export { ... } from "@nacs/shared"` — nothing else changes.
 */

/** Indian mobile numbers in E.164 (+91XXXXXXXXXX); other countries later. Same pattern as `mobileNumber` in @nacs/shared. */
const MOBILE_E164 = /^\+91[6-9]\d{9}$/;

/** Device serial as printed on the terminal and sent as DeviceID (observed: 15 alphanumerics). */
export const deviceSerial = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{4,64}$/, "serial must be 4–64 letters, digits, - or _");

export const PAIR_LIMITS = {
  name: 80,
  adminUsername: 32,
  adminPassword: 64,
  timezone: 64,
  serial: 64,
  /** People that may be named at pairing, as `pairPeopleChoice` bounds them (dto/devices.ts). */
  people: 500,
  /** Groups that may be named at pairing (`PAIR_GROUP_IDS_MAX`, dto/devices.ts). */
  groups: 50,
} as const;

/**
 * M13 §3 (ADR-0016 decision 5): who goes on the terminal the moment it is paired. The api takes a
 * union — `"everyone" | "nobody" | { personIds } | { groupIds }` — which is not a shape a radio
 * group and two pickers can hold, so the form carries the answer flat and `PairDeviceDialog` maps
 * it onto the body.
 */
export const PAIR_PEOPLE_CHOICES = ["everyone", "nobody", "pick", "groups"] as const;
export type PairPeopleChoice = (typeof PAIR_PEOPLE_CHOICES)[number];

export const pairDeviceBody = z
  .object({
    serial: deviceSerial,
    name: z.string().trim().min(1).max(PAIR_LIMITS.name),
    adminUsername: z.string().trim().min(1).max(PAIR_LIMITS.adminUsername).default("admin"),
    adminPassword: z.string().min(1).max(PAIR_LIMITS.adminPassword),
    timezone: z.string().trim().max(PAIR_LIMITS.timezone).default("Asia/Kolkata"),
    people: z.enum(PAIR_PEOPLE_CHOICES).default("everyone"),
    personIds: z.array(z.string().guid()).max(PAIR_LIMITS.people).default([]),
    groupIds: z.array(z.string().guid()).max(PAIR_LIMITS.groups).default([]),
  })
  .strict()
  .refine((v) => v.people !== "pick" || v.personIds.length > 0, {
    message: "Choose at least one person, or pick Everyone or Nobody yet",
    path: ["personIds"],
  })
  .refine((v) => v.people !== "groups" || v.groupIds.length > 0, {
    message: "Choose at least one group, or pick Everyone or Nobody yet",
    path: ["groupIds"],
  });
export type PairDeviceBody = z.infer<typeof pairDeviceBody>;
export type PairDeviceInput = z.input<typeof pairDeviceBody>;

/** Login form: accepts "9876543210", "09876543210", "+91 98765 43210" and normalises to E.164 before validation. */
export function normaliseIndianMobile(raw: string): string {
  const digits = raw.replace(/[^\d]/g, "");
  const local = digits.startsWith("91") && digits.length === 12 ? digits.slice(2) : digits.replace(/^0/, "");
  return `+91${local}`;
}

/** Same shape as `mobileNumber`, worded for a person typing it. */
const mobileField = z
  .string()
  .trim()
  .min(1, "Enter your mobile number")
  .transform(normaliseIndianMobile)
  .pipe(z.string().regex(MOBILE_E164, "Enter a 10-digit Indian mobile number"));

export const loginForm = z.object({ mobile: mobileField });
/** The visitor pass link's mobile check: the number the pass's SMS went to, normalised the same way. */
export const passLinkMobileForm = z.object({ mobile: mobileField });
export type PassLinkMobileFormInput = z.input<typeof passLinkMobileForm>;
export type PassLinkMobileFormOutput = z.output<typeof passLinkMobileForm>;
export type LoginFormInput = z.input<typeof loginForm>;
export type LoginFormOutput = z.output<typeof loginForm>;

// ── identity (mirrors dto/identity.ts) ────────────────────────────────────────────────
export const OTP_CODE_LENGTH = 6;
export const otpCode = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Enter the 6-digit code");

const uuid = z.string().guid();

export const timezoneName = z
  .string()
  .trim()
  .max(64)
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: "Unknown timezone" },
  );

/**
 * The terminal's own sign-in, changed after somebody changed it on the terminal (2026-10-04). Same
 * limits as pairing (dto/devices.ts `deviceCredentialsBody`).
 */
export const deviceCredentialsForm = z
  .object({
    adminUsername: z.string().trim().min(1, "Enter the user name").max(PAIR_LIMITS.adminUsername),
    adminPassword: z.string().min(1, "Enter the password set on the terminal").max(PAIR_LIMITS.adminPassword),
  })
  .strict();
export type DeviceCredentialsForm = z.infer<typeof deviceCredentialsForm>;

/** Rename / timezone form on the device page; the same limits as pairing (dto/devices.ts `updateDeviceBody`). */
export const updateDeviceForm = z
  .object({
    name: z.string().trim().min(1, "Give the device a name").max(PAIR_LIMITS.name),
    timezone: timezoneName,
  })
  .strict();
export type UpdateDeviceForm = z.infer<typeof updateDeviceForm>;

export const TENANT_LIMITS = { name: { min: 2, max: 80 }, timezone: 64, termsVersion: 32 } as const;
/** Sign-up records which wording its owner accepted (M6 §7, ADR-0009): the portal sends the version it showed. */
export const createTenantBody = z
  .object({
    name: z
      .string()
      .trim()
      .min(TENANT_LIMITS.name.min, "Give the organisation a name (at least 2 characters)")
      .max(TENANT_LIMITS.name.max),
    timezone: timezoneName.default("Asia/Kolkata"),
    termsVersion: z.string().trim().min(1).max(TENANT_LIMITS.termsVersion),
  })
  .strict();
export type CreateTenantInput = z.input<typeof createTenantBody>;
export type CreateTenantOutput = z.output<typeof createTenantBody>;

export const updateTenantBody = z
  .object({
    name: z
      .string()
      .trim()
      .min(TENANT_LIMITS.name.min, "Give the organisation a name (at least 2 characters)")
      .max(TENANT_LIMITS.name.max),
    timezone: timezoneName,
  })
  .strict();

/**
 * Working hours (M9 §4): what attendance measures late and early against. Same shape as the
 * `PATCH /tenants/current` body, except that an empty string here is the API's `null` ("not set"),
 * because an <input type="time"> cleared by the user gives "". Both ends are independent: with only
 * a start set, `late` is measured and `early` stays null. `weekOff` is 0 = Sunday.
 */
export const WORKING_HOURS_LIMITS = { grace: { min: 0, max: 240 } } as const;
const CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const clockTime = z
  .string()
  .trim()
  .refine((v) => v === "" || CLOCK_TIME.test(v), "Use a 24-hour time like 09:00");

export const workingHoursForm = z
  .object({
    workStart: clockTime,
    workEnd: clockTime,
    graceMinutes: z.coerce
      .number<number>()
      .int("Give whole minutes")
      .min(WORKING_HOURS_LIMITS.grace.min, "Grace cannot be negative")
      .max(WORKING_HOURS_LIMITS.grace.max, "At most 240 minutes of grace"),
    weekOff: z.array(z.number().int().min(0).max(6)).max(7),
  })
  .strict();
export type WorkingHoursFormInput = z.input<typeof workingHoursForm>;

export const REASON_MAX = 200;
const reason = z.string().trim().max(REASON_MAX).optional();

export const INVITE_LIMITS = { name: 80 } as const;
export const inviteForm = z
  .object({
    mobile: mobileField,
    name: z.string().trim().max(INVITE_LIMITS.name),
    roleId: uuid.or(z.literal("")).refine((v) => v !== "", "Choose a role"),
  })
  .strict();
export type InviteFormInput = z.input<typeof inviteForm>;
export type InviteFormOutput = z.output<typeof inviteForm>;

export const changeRoleForm = z
  .object({ roleId: uuid.or(z.literal("")).refine((v) => v !== "", "Choose a role"), reason })
  .strict();
export type ChangeRoleForm = z.infer<typeof changeRoleForm>;

export const ROLE_LIMITS = {
  name: { min: 2, max: 60 },
  permissions: 100,
  /** "Limit to devices" (M8 §3, ADR-0006 §3): null lifts it, `[]` is refused, 200 is the ceiling. */
  deviceScope: { min: 1, max: 200 },
} as const;
export const roleName = z
  .string()
  .trim()
  .min(ROLE_LIMITS.name.min, "Give the role a name (at least 2 characters)")
  .max(ROLE_LIMITS.name.max);

// ── people (mirrors dto/people.ts and dahua/person.ts `normalizeCardNo`) ──────────────
export const PERSON_LIMITS = {
  name: 80,
  externalRef: 64,
  consentNote: 200,
  cardNo: 40,
  cardName: 32,
  /** Cards that can be attached while creating a person (`cardNos` max 5). */
  cardsAtCreate: 5,
  /** People search (`q`) as the API validates it. */
  search: 80,
  /** Terminal user id (M9 §3): `^[A-Za-z0-9_-]{1,20}$` on the API. */
  deviceUserId: 20,
  /** Keypad PIN (migration 0021): `^[0-9]{4,8}$` on the API. */
  pin: 8,
  /** Access groups a person can be put into while creating them (`groupIds` max 50, M13 §2). */
  groupsAtCreate: 50,
  /** M14 §1 (ADR-0017 decision 2): `personMobileField` in dto/people.ts — a way to reach the human. */
  mobile: 32,
  /** M14 §1: `personEmailField` in dto/people.ts. */
  email: 160,
} as const;

/**
 * `POST /people/access/bulk` as the api bounds it (ADR-0016 decision 4): at most 500 people and at most
 * `PERSON_DEVICE_IDS_MAX` terminals in one call. The selection bar counts against the first, and the
 * terminal dialog never offers more rows than the organisation has, so the second is a backstop.
 */
export const BULK_ACCESS_LIMITS = { people: 500, terminals: 500 } as const;

/**
 * The terminal's own id for a person (M9 §3). The lab proved a 21-character id with letters and `-`
 * survives the terminal's records, its pushes and our event log — but the id also *names the snapshot
 * file*, so path characters are excluded. Blank means "let the platform mint one".
 */
const DEVICE_USER_ID = /^[A-Za-z0-9_-]{1,20}$/;
const deviceUserIdField = z
  .string()
  .trim()
  .max(PERSON_LIMITS.deviceUserId)
  .refine((v) => v === "" || DEVICE_USER_ID.test(v), "Letters, digits, - and _ only, up to 20 characters");

/**
 * The keypad PIN (migration 0021), typed on the terminal after the terminal user ID: 4 to 8 digits, as
 * `PIN_PATTERN` in dto/people.ts. Blank means "none" on create and "keep what they have" on edit.
 */
const PIN = /^[0-9]{4,8}$/;
const pinField = z
  .string()
  .trim()
  .max(PERSON_LIMITS.pin)
  .refine((v) => v === "" || PIN.test(v), "Use 4 to 8 digits");

export const CONSENT_BASES = ["employment", "visitor", "contract", "consent", "other"] as const;
export const consentBasis = z.enum(CONSENT_BASES);
export const personStatus = z.enum(["active", "disabled"]);

/** Card numbers as the terminal keys them: upper-case hex, separators dropped; null when it is not one. */
export function normaliseCardNo(raw: string): string | null {
  const v = raw.replace(/[\s:-]/g, "").toUpperCase();
  return /^[0-9A-F]{4,32}$/.test(v) ? v : null;
}
const CARD_NO_MESSAGE = "Card numbers are 4–32 hex digits; spaces, colons and dashes are ignored";

/** Required card number (the add-card form). Output is the normalised value. */
export const cardNoField = z
  .string()
  .trim()
  .min(1, "Enter the card number")
  .max(PERSON_LIMITS.cardNo)
  .refine((v) => normaliseCardNo(v) !== null, CARD_NO_MESSAGE);
/** Optional card number (a repeatable row on the create form): empty rows are dropped on submit. */
const cardNoOptional = z
  .string()
  .trim()
  .max(PERSON_LIMITS.cardNo)
  .refine((v) => v === "" || normaliseCardNo(v) !== null, CARD_NO_MESSAGE);

/** "YYYY-MM-DD" from a date input, or empty. Converted to an ISO instant in the organisation timezone on submit. */
const orgDateInput = z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/, "Enter a date");

/**
 * M14 §1 (ADR-0017 decision 2): a way to reach the human, nothing more — never required. Mirrors
 * `personMobileField` in dto/people.ts, looser than the login `mobileField` above (this one is a front
 * desk typing in whatever a person, a guardian or an employer handed them, not a +91 login credential).
 */
const PERSON_MOBILE_RE = /^[0-9+()\-.\s]{3,32}$/;
/**
 * Exported since M17: a gym LEAD carries the same two contact fields and `dto/leads.ts` validates
 * them with the very same `personMobileField` / `personEmailField`, because a lead's number is
 * compared against `people.mobile` by the duplicate warning and carried into the person the
 * conversion creates. Two spellings of one rule would surface as a 422 at the worst possible moment.
 */
export const personMobileOptional = z
  .string()
  .trim()
  .max(PERSON_LIMITS.mobile)
  .refine((v) => v === "" || PERSON_MOBILE_RE.test(v), "Use digits, spaces, +, - or ( ) only");
/** Mirrors `personEmailField`; case is not significant (the api lower-cases it for matching). Exported for M17's lead capture, beside the mobile above. */
export const personEmailOptional = z
  .string()
  .trim()
  .max(PERSON_LIMITS.email)
  .refine((v) => v === "" || z.string().email().safeParse(v).success, "Enter a valid e-mail address");

const personFields = {
  name: z.string().trim().min(1, "Give the person a name").max(PERSON_LIMITS.name),
  externalRef: z.string().trim().max(PERSON_LIMITS.externalRef),
  mobile: personMobileOptional,
  email: personEmailOptional,
  deviceUserId: deviceUserIdField,
  pin: pinField,
  validFrom: orgDateInput,
  validTo: orgDateInput,
  consentBasis: consentBasis.or(z.literal("")),
  consentNote: z.string().trim().max(PERSON_LIMITS.consentNote),
};
type PersonFieldValues = { [K in keyof typeof personFields]: z.input<(typeof personFields)[K]> };

function personRules(v: PersonFieldValues, ctx: z.RefinementCtx): void {
  if (v.validFrom && v.validTo && v.validTo < v.validFrom) {
    ctx.addIssue({ code: "custom", path: ["validTo"], message: "Must be on or after the start date" });
  }
  if (v.consentNote && !v.consentBasis) {
    ctx.addIssue({ code: "custom", path: ["consentBasis"], message: "Choose the basis for this note" });
  }
}

/** Create dialog: consent is recorded at enrolment (ADR-0009 §2), so the basis is required here. */
export const createPersonForm = z
  .object({
    ...personFields,
    // Explicit boolean: an inferred type predicate would narrow the output type away from the form's "" default.
    consentBasis: consentBasis
      .or(z.literal(""))
      .refine((v): boolean => v !== "", "Choose the basis for consent"),
    cards: z.array(z.object({ value: cardNoOptional }).strict()).max(PERSON_LIMITS.cardsAtCreate),
  })
  .strict()
  .superRefine(personRules);
export type CreatePersonForm = z.infer<typeof createPersonForm>;

/**
 * Detail page: the same fields plus status; consent may be absent (people enrolled on the terminal).
 * `clearPin` is the "Remove the PIN" tick: a PIN can never be shown back, so removing one is a choice of
 * its own rather than an emptied box (an empty box means "keep it").
 */
export const updatePersonForm = z
  .object({ ...personFields, status: personStatus, clearPin: z.boolean() })
  .strict()
  .superRefine(personRules);
export type UpdatePersonForm = z.infer<typeof updatePersonForm>;

// ── schedules and holiday groups (M9 §5, mirrors dto/schedules.ts) ────────────────────
export const SCHEDULE_LIMITS = {
  name: 60,
  /** Periods a day, as the terminal stores them. */
  periods: 4,
  /** Dated ranges in one holiday list. */
  holidayDays: 16,
  holidayDayName: 32,
} as const;

const clockPeriod = z
  .object({
    from: z.string().regex(CLOCK_TIME, "Use a 24-hour time like 09:00"),
    to: z.string().regex(CLOCK_TIME, "Use a 24-hour time like 18:00"),
  })
  .strict()
  .refine((p) => p.to > p.from, { message: "The end must be after the start", path: ["to"] });

/**
 * Seven days Sunday-first, at most four periods each — the terminal's own shape. A day with no periods
 * is a closed day, which is a real answer and not an error: a weekday-only template is exactly that.
 */
export const scheduleForm = z
  .object({
    name: z.string().trim().min(1, "Give the schedule a name").max(SCHEDULE_LIMITS.name),
    weekly: z.array(z.array(clockPeriod).max(SCHEDULE_LIMITS.periods)).length(7),
  })
  .strict();
export type ScheduleForm = z.infer<typeof scheduleForm>;

const holidayDay = z
  .object({
    from: orgDateInput.refine((v) => v !== "", "Choose a date"),
    to: orgDateInput.refine((v) => v !== "", "Choose a date"),
    name: z.string().trim().min(1, "Name the holiday").max(SCHEDULE_LIMITS.holidayDayName),
  })
  .strict()
  .refine((d) => d.to >= d.from, { message: "The end must be on or after the start", path: ["to"] });

export const holidayGroupForm = z
  .object({
    name: z.string().trim().min(1, "Give the list a name").max(SCHEDULE_LIMITS.name),
    days: z.array(holidayDay).max(SCHEDULE_LIMITS.holidayDays),
  })
  .strict();
export type HolidayGroupForm = z.infer<typeof holidayGroupForm>;

// ── access groups (M13 §3, mirrors dto/accessGroups.ts) ──────────────────────────────
/** Same lengths the api validates; at most 500 groups per organisation (the api answers 409). */
export const ACCESS_GROUP_LIMITS = {
  name: 80,
  description: 500,
  /** People named per side in one membership call (`ACCESS_GROUP_MEMBERS_PER_CALL_MAX`). */
  membersPerCall: 500,
} as const;

/** New group and the rename form on the detail page: one name, one optional sentence about it. */
export const accessGroupForm = z
  .object({
    name: z.string().trim().min(1, "Give the group a name").max(ACCESS_GROUP_LIMITS.name),
    description: z.string().trim().max(ACCESS_GROUP_LIMITS.description),
  })
  .strict();
export type AccessGroupForm = z.infer<typeof accessGroupForm>;

export const addCardForm = z
  .object({ cardNo: cardNoField, name: z.string().trim().max(PERSON_LIMITS.cardName) })
  .strict();
export type AddCardForm = z.infer<typeof addCardForm>;

// ── time-bounded access grants (M14 §2, ADR-0018; mirrors dto/accessGroups.ts) ─────────
/** `access_group_grants.source` — the gym requirements' ACC-004 "reason" (ADR-0018 decision 1). */
export const GRANT_SOURCES = ["manual", "gym_membership", "staff", "student", "booking", "day_pass"] as const;
/** `pending | active | expired | revoked` — readings of the dates and `revokedAt`, never stored (decision 4). */
export const GRANT_STATES = ["pending", "active", "expired", "revoked"] as const;
/**
 * `GrantWindowDialog` and `GrantRevokeDialog` are plain-state confirmations (the `ReasonDialog`
 * pattern), not react-hook-form forms — one or two fields, no `zodResolver` needed — so these limits
 * are read directly on the input rather than through a Zod schema.
 */
export const GRANT_LIMITS = { note: 500, reason: 200 } as const;

// ── platform (mirrors dto/platform.ts: API keys, organisation deletion) ────────────────
export const API_KEY_LIMITS = { name: 60, scopes: 20 } as const;

/** Create-key dialog: the expiry is typed as an organisation day and converted with `orgDateToIso` on submit. */
export const createApiKeyForm = z
  .object({
    name: z.string().trim().min(1, "Give the key a name").max(API_KEY_LIMITS.name),
    scopes: z.array(z.string()).min(1, "Choose at least one scope").max(API_KEY_LIMITS.scopes),
    expiresOn: orgDateInput,
  })
  .strict();
export type CreateApiKeyForm = z.infer<typeof createApiKeyForm>;

/** Deleting an organisation: the typed name must match exactly (the API compares it too). */
export const DELETE_ORGANISATION_LIMITS = { confirmName: 80 } as const;

// ── billing (mirrors dto/billing.ts `billingProfileBody`) ─────────────────────────────
export const BILLING_PROFILE_LIMITS = {
  legalName: 160,
  addressLine: 160,
  city: 80,
  state: 80,
  postcode: 6,
  country: 2,
  gstin: 15,
  billingEmail: 160,
} as const;

/**
 * GSTIN: 2 state digits, 5 PAN letters, 4 PAN digits, 1 PAN letter, 1 entity character, a literal Z,
 * 1 checksum character. Verbatim from `@nacs/shared` `GSTIN_REGEX`; the field is uppercased first and
 * an empty field is allowed, because not every small business is registered.
 */
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/**
 * The billing details Razorpay puts on the GST invoice (ADR-0012). Optional fields are blank strings
 * in the form and dropped on submit, so a cleared field is sent as "not set" and not as "".
 */
export const billingProfileForm = z
  .object({
    legalName: z
      .string()
      .trim()
      .min(1, "Enter the registered name of the business")
      .max(BILLING_PROFILE_LIMITS.legalName),
    addressLine1: z
      .string()
      .trim()
      .min(1, "Enter the street address")
      .max(BILLING_PROFILE_LIMITS.addressLine),
    addressLine2: z.string().trim().max(BILLING_PROFILE_LIMITS.addressLine),
    city: z.string().trim().min(1, "Enter the city").max(BILLING_PROFILE_LIMITS.city),
    state: z.string().trim().min(1, "Enter the state").max(BILLING_PROFILE_LIMITS.state),
    postcode: z
      .string()
      .trim()
      .regex(/^[0-9]{6}$/, "Enter a six-digit PIN code"),
    country: z.string().trim().length(2, "Two-letter country code"),
    gstin: z
      .string()
      .trim()
      .transform((s) => s.toUpperCase())
      .refine((s) => s === "" || GSTIN_REGEX.test(s), "That is not a valid GSTIN"),
    billingEmail: z
      .string()
      .trim()
      .max(BILLING_PROFILE_LIMITS.billingEmail)
      .refine((s) => s === "" || z.string().email().safeParse(s).success, "Enter a valid email address"),
  })
  .strict();
export type BillingProfileForm = z.infer<typeof billingProfileForm>;

// ── outbound webhooks (mirrors dto/webhooks.ts) ───────────────────────────────────────
export const WEBHOOK_LIMITS = {
  url: 2048,
  description: 200,
  /** Deliveries per minute (M8 §6): the api's CHECK and its default, mirrored (rules/00 §1.5). */
  rate: { min: 10, max: 600, default: 120 },
} as const;

/** The api's own words when an endpoint would be left subscribed to nothing (422, plan §6). */
export const WEBHOOK_NO_SUBSCRIPTION = "Subscribe to at least one event type, or to the hourly digest";

/**
 * HTTPS only. The api answers `422 WEBHOOK_URL_REFUSED` for anything else — scheme included — after
 * resolving the hostname (ADR-0013 SSRF policy); this refuses the scheme here so the customer is told
 * before the round trip, and the api stays the boundary for everything a regex cannot see.
 */
export const webhookEndpointForm = z
  .object({
    url: z
      .string()
      .trim()
      .min(1, "Enter the address that should receive the events")
      .max(WEBHOOK_LIMITS.url)
      .url("Enter a full address, starting with https://")
      .refine((u) => u.startsWith("https://"), "Only https:// addresses can receive webhooks"),
    description: z.string().trim().max(WEBHOOK_LIMITS.description),
    /**
     * The realtime types only. `access.digest.hourly` is NOT one of them — it is a subscription of
     * its own, so an endpoint may carry `events: []` with `digestHourly: true` (M8 §6). The api
     * refuses either list on its own being empty with 422; the refine below says so first.
     */
    events: z.array(z.string()),
    digestHourly: z.boolean(),
    maxPerMinute: z
      .number({
        error: (issue) =>
          issue.code === "invalid_type"
            ? `Enter a number between ${WEBHOOK_LIMITS.rate.min} and 600`
            : undefined,
      })
      .int(`Enter a whole number between ${WEBHOOK_LIMITS.rate.min} and 600`)
      .min(WEBHOOK_LIMITS.rate.min, `At least ${WEBHOOK_LIMITS.rate.min} deliveries per minute`)
      .max(WEBHOOK_LIMITS.rate.max, `At most ${WEBHOOK_LIMITS.rate.max} deliveries per minute`),
  })
  .strict()
  .refine((v) => v.events.length > 0 || v.digestHourly, {
    message: WEBHOOK_NO_SUBSCRIPTION,
    path: ["events"],
  });
export type WebhookEndpointForm = z.infer<typeof webhookEndpointForm>;

// ── the member ledger (M14 §4, ADR-0019; mirrors dto/ledger.ts) ───────────────────────
/** The CHECK-less method and source lists (migration 0031 left them without one on purpose): an
 *  organisation adding either must not need a migration, so these are plain arrays, not a closed enum. */
export const LEDGER_METHODS = ["cash", "upi", "card", "bank_transfer", "other"] as const;
export const LEDGER_METHOD_LABELS: Record<(typeof LEDGER_METHODS)[number], string> = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  bank_transfer: "Bank transfer",
  other: "Other",
};
export const LEDGER_ADJUSTMENT_KINDS = ["adjustment", "refund", "write_off"] as const;
/** `amount_paise` is `integer` in migration 0031 with `> 0` checked; one entry caps at about ₹2.14 crore. */
export const LEDGER_MAX_AMOUNT_PAISE = 2_147_483_647;
export const LEDGER_LIMITS = { note: 500, adjustmentNote: 3, reference: 120, reason: 200 } as const;

const ledgerNote = z.string().trim().max(LEDGER_LIMITS.note);
const ledgerReference = z.string().trim().max(LEDGER_LIMITS.reference);

/**
 * What the front desk types — rupees, as a decimal, the way every price on this screen already reads
 * (`formatMoney`) — kept as a STRING through validation (never `z.preprocess`: that types the field
 * `unknown` for react-hook-form, which cannot bind a controlled input to it). The dialog converts to
 * the integer paise the api's own bound is stated in (`amount_paise`, ADR-0012: rounding a currency
 * twice is how money goes missing) only when it builds the request body, with `rupeesStringToPaise`.
 */
export function isValidRupeeAmount(value: string): boolean {
  return /^\d+(\.\d{1,2})?$/.test(value.trim());
}
export function rupeesStringToPaise(value: string): number {
  return Math.round(Number(value.trim()) * 100);
}
const ledgerAmountField = z
  .string()
  .trim()
  .min(1, "Enter an amount")
  .refine(isValidRupeeAmount, "Enter an amount like 250 or 250.50")
  .refine((v) => !isValidRupeeAmount(v) || rupeesStringToPaise(v) >= 1, "Enter an amount greater than ₹0")
  .refine(
    (v) => !isValidRupeeAmount(v) || rupeesStringToPaise(v) <= LEDGER_MAX_AMOUNT_PAISE,
    "That amount is too large",
  );

/**
 * Fields every ledger write shares (mirrors `entryFields` server-side). `source`/`sourceRef` are
 * deliberately absent: every write this portal makes is an operator at the desk, which the api
 * already defaults to `source: "manual"` — a module (a gym membership, a booking) writes its own
 * sourced entries directly and never through this form.
 */
const ledgerEntryFields = {
  amount: ledgerAmountField,
  /** An organisation day (`YYYY-MM-DD`); the dialog resolves it to the api's own instant, never a future one. */
  occurredAt: z.string().optional(),
  note: ledgerNote.optional(),
  reference: ledgerReference.optional(),
};

/** Raises what the person owes. No receipt number: a charge is a bill, not money received. */
export const ledgerChargeForm = z.object(ledgerEntryFields).strict();
export type LedgerChargeForm = z.infer<typeof ledgerChargeForm>;

/** Lowers what the person owes; `method` is required because the till reconciliation groups by it. */
export const ledgerPaymentForm = z.object({ ...ledgerEntryFields, method: z.enum(LEDGER_METHODS) }).strict();
export type LedgerPaymentForm = z.infer<typeof ledgerPaymentForm>;

/**
 * `ledger:adjust`, not `ledger:record`: changing what somebody owes is a different power from taking
 * their money. `note` is required and not optional prose — it is what the Reversals-and-adjustments
 * report shows an owner who suspects someone at the desk — and a refund must say how the money went back.
 */
export const ledgerAdjustmentForm = z
  .object({
    ...ledgerEntryFields,
    kind: z.enum(LEDGER_ADJUSTMENT_KINDS),
    note: ledgerNote.min(LEDGER_LIMITS.adjustmentNote, "Say why — this is what a report reads back"),
    method: z.enum(LEDGER_METHODS).optional(),
  })
  .strict()
  .refine((v) => v.kind !== "refund" || v.method !== undefined, {
    message: "A refund must say how the money was returned",
    path: ["method"],
  });
export type LedgerAdjustmentForm = z.infer<typeof ledgerAdjustmentForm>;

/**
 * `LedgerReverseDialog` is a plain-state confirmation (the `ReasonDialog` pattern, required rather
 * than optional) — one field, no `zodResolver` needed — so `LEDGER_LIMITS.reason` is read directly on
 * the input rather than through a Zod schema here.
 */

// ── the Gym module (M15, ADR-0017): plans and the membership lifecycle ──────────────
/**
 * Mirrors `@nacs/shared` `dto/gym.ts`'s own bounds (`note`/`reason` max 500, one extension caps at a
 * year, grace caps at 90 days — `GYM_MAX_GRACE_DAYS` — and `memberNoPrefix` at 12).
 */
export const GYM_LIMITS = {
  name: 120,
  note: 500,
  reason: 500,
  extendDaysMax: 365,
  memberNo: 40,
  graceDaysMax: 90,
  overdueGraceDaysMax: 90,
  memberNoPrefix: 12,
  /** `GYM_TRAINER_GROUPS_MAX` / `GYM_TRAINER_GROUPS_PER_TRAINER_MAX` in `@nacs/shared` (0048). */
  trainerGroupsMax: 12,
  /** How many EXTRA door sets one member may hold (migration 0050) — the same bound, same reason. */
  memberGroupsMax: 12,
  /**
   * `LEAD_RETENTION_MONTHS_*` in `@nacs/shared` (M17 plan §8, migration 0049's
   * `gym_settings_lead_retention_check`): **12 months by default**, confirmed by Kumar on
   * 2026-09-22 — long enough for a seasonal enquirer to come back in January, short enough to be a
   * real answer to "how long do you keep my number".
   */
  leadRetentionMonthsDefault: 12,
  leadRetentionMonthsMin: 3,
  leadRetentionMonthsMax: 60,
} as const;

const gymNote = z.string().trim().max(GYM_LIMITS.note);
/** Same shape as the ledger's own amount field: rupees as a string until the dialog builds the body. */
const gymAmountField = z
  .string()
  .trim()
  .refine((v) => v === "" || isValidRupeeAmount(v), "Enter an amount like 250 or 250.50")
  .optional();
/** The whole-form settings screen always submits this one, so it is a plain (non-optional) string field. */
const gymSettingsAmountField = z
  .string()
  .trim()
  .refine((v) => v === "" || isValidRupeeAmount(v), "Enter an amount like 250 or 250.50");

/**
 * **The doors half of a gym form** (2026-09-21) — Kumar: *"may give option to select multiple
 * terminals here itself or may give option to select terminal and create group, as GYM user may not
 * be aware of terminal group"*.
 *
 * `doorsMode` is a radio and never inferred from whether the terminal list happens to be empty, for
 * the reason `personMode` on the registration form is one: the api refuses a body carrying both
 * `terminals` and `accessGroupId`, and a form that guessed which the operator meant would be the
 * ambiguity the refusal exists to prevent.
 *
 * Both branches are validated where the operator can see it, so the two ways of saying nothing —
 * "terminals, none ticked" and "a group, none chosen" — each get their own sentence rather than a
 * 422 from the api.
 */
export const gymTerminalSelectionForm = z
  .object({
    deviceId: z.string().min(1),
    /** 0-based door indexes, as the terminal numbers them. At least one, or the row grants nothing. */
    doors: z.array(z.number().int().min(0)).min(1),
  })
  .strict();
export type GymTerminalSelectionForm = z.infer<typeof gymTerminalSelectionForm>;

const gymDoorsFields = {
  doorsMode: z.enum(["terminals", "group"]),
  terminals: z.array(gymTerminalSelectionForm),
  accessGroupId: z.string(),
};
interface GymDoorsFormValues {
  doorsMode: "terminals" | "group";
  terminals: GymTerminalSelectionForm[];
  accessGroupId: string;
}
/** The two ways of saying nothing, each with its own sentence under the branch that said it. */
const ticked = (v: GymDoorsFormValues): boolean => v.doorsMode !== "terminals" || v.terminals.length > 0;
const chosen = (v: GymDoorsFormValues): boolean => v.doorsMode !== "group" || v.accessGroupId.length > 0;
const TICK_A_TERMINAL = { message: "Tick at least one terminal", path: ["terminals"] };
const CHOOSE_A_GROUP = { message: "Choose an access group", path: ["accessGroupId"] };

export const createGymPlanForm = z
  .object({
    name: z.string().trim().min(1, "Name the plan").max(GYM_LIMITS.name),
    description: gymNote.optional(),
    durationUnit: z.enum(["months", "days"]),
    duration: z.coerce.number<number>().int().min(1, "Enter a duration"),
    price: z
      .string()
      .trim()
      .min(1, "Enter a price")
      .refine(isValidRupeeAmount, "Enter an amount like 250 or 250.50"),
    ...gymDoorsFields,
  })
  .strict()
  .refine(ticked, TICK_A_TERMINAL)
  .refine(chosen, CHOOSE_A_GROUP);
export type CreateGymPlanForm = z.infer<typeof createGymPlanForm>;

/**
 * **A named door set a gym offers its trainers** (2026-09-21) — Kumar: *"it may vary by trainer — go
 * with multiple trainer groups."*
 *
 * The same `gymDoorsFields` trio as a plan's, because it is the same question asked about a
 * different thing, plus the one field only this shape has: a NAME. A plan's group is named after the
 * plan; nothing else names a trainer set, so the operator's own words are the group's name — and a
 * set created from terminals therefore cannot be nameless, which the api refuses too.
 */
export const gymTrainerGroupForm = z
  .object({
    name: z.string().trim().max(GYM_LIMITS.name),
    ...gymDoorsFields,
  })
  .strict()
  .refine(ticked, TICK_A_TERMINAL)
  .refine(chosen, CHOOSE_A_GROUP)
  .refine((v) => v.doorsMode !== "terminals" || v.name.length > 0, {
    message: "Name this set — it is what the Add-a-trainer dialog and Access Control both show",
    path: ["name"],
  });
export type GymTrainerGroupForm = z.infer<typeof gymTrainerGroupForm>;

/** The doors on their own — the dialog that edits an existing plan's terminals and nothing else. */
export const gymPlanDoorsForm = z
  .object(gymDoorsFields)
  .strict()
  .refine(ticked, TICK_A_TERMINAL)
  .refine(chosen, CHOOSE_A_GROUP);
export type GymPlanDoorsForm = z.infer<typeof gymPlanDoorsForm>;

/** Assign (MSH-001) and renew (MSH-002, BR-05) share every field; renewing defaults to the same plan. */
export const assignGymMembershipForm = z
  .object({
    planId: z.string().min(1, "Choose a plan"),
    startsOn: z.string().optional(),
    /** Overrides the plan's list price for this sale only; left blank, the plan's own price is charged. */
    priceOverride: gymAmountField,
    discount: gymAmountField,
    note: gymNote.optional(),
    // Not `.default(true)`: a default makes the schema's INPUT and OUTPUT types diverge (`boolean |
    // undefined` vs `boolean`), which `zodResolver` cannot reconcile under `exactOptionalPropertyTypes`.
    // `defaultValues` on the form already seeds it `true`, which is all the default ever did here.
    charge: z.boolean(),
  })
  .strict();
export type AssignGymMembershipForm = z.infer<typeof assignGymMembershipForm>;

/**
 * **Joining the gym, as ONE form** (2026-09-21) — `POST /gym/registrations`.
 *
 * Kumar: *"Gym module user will why need to go to access control add user and come back here? This
 * is making unnecessarily complex. They add all details here and we handle it."* Registering a
 * walk-in used to be three acts in two workspaces; this is the one act, and it carries all three
 * halves of the API body.
 *
 * `personMode` is the branch the api expresses as `personId` XOR `person`, and it is a radio rather
 * than an inference: a form that guessed which one the operator meant from whether a search box
 * happened to be empty is exactly the ambiguity the api refuses.
 *
 * **`planId` may be blank**, and that is "no plan yet" rather than an unfinished form: a walk-in who
 * wants to think about it is registered with no grant, no charge and no door, which is what the
 * narrow `POST /gym/members` has always done.
 */
export const registerGymMemberForm = z
  .object({
    personMode: z.enum(["existing", "new"]),
    /** The picked person, when `personMode` is "existing". */
    personId: z.string(),
    // The new person's own fields: the name and the two SHARED contact columns (ADR-0017 decision
    // 2). No PIN, no card, no photo and no terminal — a module mints no credential (decision 5), and
    // the dialog says so at the foot rather than leaving the desk to find out at the reader.
    name: z.string().trim().max(PERSON_LIMITS.name),
    externalRef: z.string().trim().max(PERSON_LIMITS.externalRef),
    mobile: personMobileOptional,
    email: personEmailOptional,
    // The gym's own profile of them, every field optional exactly as the api has it.
    /**
     * **2026-09-22: the doors this member reaches BEYOND the ones their plan opens** (migration
     * 0050) — Kumar: *"same for gym member as well. should give Group and Device access option here
     * itself"*. Ids of the gym's own door sets; granted on the membership's window, so they close
     * when it does. Empty is a perfectly good answer and is the ordinary one.
     */
    accessGroupIds: z.array(z.string().min(1)).max(GYM_LIMITS.memberGroupsMax),
    memberNo: z.string().trim().max(GYM_LIMITS.memberNo),
    joinedOn: z.string(),
    trainerPersonId: z.string(),
    notes: gymNote,
    // The first membership — the half that writes the grant and therefore opens a door.
    planId: z.string(),
    startsOn: z.string(),
    priceOverride: z
      .string()
      .trim()
      .refine((v) => v === "" || isValidRupeeAmount(v), "Enter an amount like 250 or 250.50"),
    discount: z
      .string()
      .trim()
      .refine((v) => v === "" || isValidRupeeAmount(v), "Enter an amount like 250 or 250.50"),
    charge: z.boolean(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.personMode === "existing" && !v.personId)
      ctx.addIssue({ code: "custom", path: ["personId"], message: "Choose who is joining" });
    if (v.personMode === "new" && !v.name)
      ctx.addIssue({ code: "custom", path: ["name"], message: "Give the person a name" });
  });
export type RegisterGymMemberForm = z.infer<typeof registerGymMemberForm>;

/**
 * **HIRING A TRAINER, AS ONE FORM** (2026-09-21) — Kumar: *"same logic for Trainers add as well"*,
 * one screen over from the registration above.
 *
 * The first six fields are the same six, with the same names and the same rules, and that is not a
 * coincidence to be tidied away later: `GymPersonField` renders them for BOTH forms through
 * `GymPersonFieldValues`, the way `PersonFields` already serves the enrolment dialog and the person
 * page. A second spelling of "mobile is optional but must look like a number" is a second answer to
 * one question.
 *
 * What a trainer does NOT carry is everything the member half adds: no member number, no plan, no
 * price. Hiring writes a person and a roster row — never a `gym_members` row, because the add-on's
 * counter counts those and a trainer occupying one would be staff billed as a customer.
 */
export const addGymTrainerForm = z
  .object({
    personMode: z.enum(["existing", "new"]),
    personId: z.string(),
    name: z.string().trim().max(PERSON_LIMITS.name),
    externalRef: z.string().trim().max(PERSON_LIMITS.externalRef),
    mobile: personMobileOptional,
    email: personEmailOptional,
    /** What the front desk should know about them: specialty, hours. */
    note: gymNote,
    /**
     * **Which doors this coach reaches** (2026-09-21) — the field that keeps the dialog from sending
     * the operator to Access Control, which is what its own info panel used to do.
     *
     * Ids of access groups: the gym's own trainer sets, and any hand-made group the organisation
     * already keeps. NOT required, and an empty list is a perfectly good answer — "decide later" and
     * "no doors yet" are both real, and the api treats an omitted list and an empty one the same way
     * on a fresh hire. Changing it afterwards is the Access dialog on the roster row.
     */
    accessGroupIds: z.array(z.string().min(1)).max(GYM_LIMITS.trainerGroupsMax),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.personMode === "existing" && !v.personId)
      ctx.addIssue({ code: "custom", path: ["personId"], message: "Choose who is joining the roster" });
    if (v.personMode === "new" && !v.name)
      ctx.addIssue({ code: "custom", path: ["name"], message: "Give the trainer a name" });
  });
export type AddGymTrainerForm = z.infer<typeof addGymTrainerForm>;

/**
 * **This trainer's gym-issued groups become exactly these** — the dialog behind the roster's Access
 * action, and the path for changing a coach's doors after the hire.
 *
 * A DECLARATION rather than an add: a group unticked here has its gym-issued grant revoked, and `[]`
 * means "no gym-issued access", which an operator can genuinely mean. What it never touches is a
 * grant the gym did not issue — their employment's, or a group somebody ticked them into in Access
 * Control — so the form has nothing to say about those and deliberately does not show them as
 * unticked boxes.
 */
/**
 * **A member's EXTRA door sets become exactly these** (2026-09-22) — the dialog behind the member
 * card's "Extra doors", and the field the register dialog carries.
 *
 * The same declaration shape as the trainer's and the same `[]` meaning, with the one difference
 * that is the whole design: these ride the MEMBERSHIP's window and close when it does, so the copy
 * around the control says so. The plan's own group is never in this list — it is not this screen's
 * to change.
 */
export const gymMemberAccessForm = z
  .object({ accessGroupIds: z.array(z.string().min(1)).max(GYM_LIMITS.memberGroupsMax) })
  .strict();
export type GymMemberAccessForm = z.infer<typeof gymMemberAccessForm>;

export const gymTrainerAccessForm = z
  .object({ accessGroupIds: z.array(z.string().min(1)).max(GYM_LIMITS.trainerGroupsMax) })
  .strict();
export type GymTrainerAccessForm = z.infer<typeof gymTrainerAccessForm>;

/**
 * **Ending a trainer assignment** (`DELETE /gym/members/{id}/trainer`). One field, and it is the
 * whole reason the verb exists separately from "clear the trainer": `endedOn` is the LAST DAY the
 * trainer looked after them, inclusive, and the correction "she actually stopped last Monday" is the
 * case that earns it. Blank means the organisation's today, which the api fills in.
 */
export const endGymTrainerForm = z.object({ endedOn: orgDateInput }).strict();
export type EndGymTrainerForm = z.infer<typeof endGymTrainerForm>;

export const extendGymMembershipForm = z
  .object({
    days: z.coerce
      .number<number>()
      .int()
      .min(1, "Enter at least 1 day")
      .max(GYM_LIMITS.extendDaysMax, `At most ${GYM_LIMITS.extendDaysMax} days`),
    reason: z.string().trim().min(1, "Say why you are extending it").max(GYM_LIMITS.reason),
  })
  .strict();
export type ExtendGymMembershipForm = z.infer<typeof extendGymMembershipForm>;

/**
 * `POST /gym/memberships/:id/reschedule` (2026-10-16): a "Starts later" membership sold with the wrong start day.
 * The end moves with it (same length) and nothing is charged or refunded. "Not before today" depends on the
 * organisation's today, so the dialog checks it; the api refuses it too (`GYM_START_IN_PAST`).
 */
export const rescheduleGymMembershipForm = z
  .object({
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick the new start date"),
    reason: z.string().trim().min(1, "Say why the start is changing").max(GYM_LIMITS.reason),
  })
  .strict();
export type RescheduleGymMembershipForm = z.infer<typeof rescheduleGymMembershipForm>;

/**
 * The gym's own settings (GYM-002, GYM-003): a whole-form PUT, so every field is always submitted —
 * `entryGroupId` is `""` for "no entry group set" (SearchableSelect's own "nothing chosen"), converted
 * to `null` when the page builds the body. `graceDays` moves the GRANT's window, never the
 * membership's contractual end date (BR-07) — the settings screen's own copy says so, because the
 * distinction is the whole reason "Sold until" and "Door open until" are two different facts on the
 * member card.
 */
export const gymSettingsForm = z
  .object({
    name: z.string().trim().max(GYM_LIMITS.name).optional(),
    /**
     * 2026-09-21: the entry set, said either way. `doorsMode: "terminals"` with an empty
     * `terminals` is the legitimate "no entry doors set" — the state this screen has always been
     * allowed to be in — so neither branch is required here, unlike a plan's, which must open
     * something to be worth selling.
     */
    ...gymDoorsFields,
    blockOnExpiry: z.boolean(),
    graceDays: z.coerce
      .number<number>()
      .int()
      .min(0)
      .max(GYM_LIMITS.graceDaysMax, `At most ${GYM_LIMITS.graceDaysMax} days`),
    blockOnOverdue: z.boolean(),
    overdueBlockAmount: gymSettingsAmountField,
    /**
     * Only a NUMBER here: the grace is held to whole days within 0–`overdueGraceDaysMax` below, and only while
     * the block is on (`blockOnOverdue`). When it is off the field is disabled, and a disabled field must never
     * stop the rest of the form saving. A value that is absent altogether would coerce to NaN, so the form never
     * starts without one: `gymFormFrom` reads a missing `overdueGraceDays` (an older api) as 0.
     */
    overdueGraceDays: z.coerce.number<number>(),
    memberNoPrefix: z.string().trim().max(GYM_LIMITS.memberNoPrefix).optional(),
    /**
     * M17 plan §8: how long a lead nobody has touched is kept before the nightly sweep DELETES it
     * and its activity log. The bounds are the api's own (`gym_settings_lead_retention_check`), so
     * a gym that wants three months gets it and one that types 600 is told here rather than by a
     * constraint violation — and the message says what the number does, because this is the one
     * field on the screen that destroys data.
     */
    leadRetentionMonths: z.coerce
      .number<number>()
      .int()
      .min(
        GYM_LIMITS.leadRetentionMonthsMin,
        `Keep enquiries at least ${GYM_LIMITS.leadRetentionMonthsMin} months`,
      )
      .max(GYM_LIMITS.leadRetentionMonthsMax, `At most ${GYM_LIMITS.leadRetentionMonthsMax} months`),
  })
  .strict()
  .superRefine((form, ctx) => {
    if (!form.blockOnOverdue || isGymOverdueGrace(form.overdueGraceDays)) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["overdueGraceDays"],
      message:
        form.overdueGraceDays > GYM_LIMITS.overdueGraceDaysMax
          ? `At most ${GYM_LIMITS.overdueGraceDaysMax} days`
          : "Whole days, 0 or more",
    });
  });
export type GymSettingsForm = z.infer<typeof gymSettingsForm>;

/** A dues grace the api accepts: whole days, 0 to `GYM_LIMITS.overdueGraceDaysMax`. */
export function isGymOverdueGrace(days: number): boolean {
  return Number.isInteger(days) && days >= 0 && days <= GYM_LIMITS.overdueGraceDaysMax;
}
